import i18next from "i18next";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  CheckIcon,
  CountryName,
  Dropdown,
  DropdownOption,
  Flag,
  IconContainer,
  Input,
  NoResults,
  SectionHeader,
  SelectContainer,
  SelectWrapper,
} from "./style";

export const SearchableSelect = ({
  value,
  onChange,
  options = [],
  placeholder = "Поиск...",
  icon,
  disabled = false,
}) => {
  const { t } = i18next;
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  const selectedOption = useMemo(() => options.find(opt => opt.code === value) || null, [options, value]);

  useEffect(() => {
    if (!isOpen && selectedOption) {
      setSearchTerm(selectedOption.name);
    }
  }, [isOpen, selectedOption]);

  const { defaultCountries, regularCountries } = useMemo(() => {
    const lowerSearchTerm = searchTerm.toLowerCase();

    const filtered = options.filter(option => option.name.toLowerCase().includes(lowerSearchTerm));

    const defaults = filtered.filter(opt => opt.default_country === "true");

    return {
      defaultCountries: defaults,
      regularCountries: filtered.slice(0, 20),
    };
  }, [options, searchTerm]);

  const handleClickOutside = useCallback(
    event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm(selectedOption?.name || "");
      }
    },
    [selectedOption]
  );

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [handleClickOutside]);

  const handleInputChange = useCallback(
    e => {
      const newValue = e.target.value;
      setSearchTerm(newValue);

      if (!isOpen) setIsOpen(true);
      if (!newValue) onChange("");
    },
    [isOpen, onChange]
  );

  const handleOptionClick = useCallback(
    option => {
      setSearchTerm(option.name);
      setIsOpen(false);
      onChange(option.code);
    },
    [onChange]
  );

  const handleInputFocus = useCallback(() => {
    if (!disabled) setIsOpen(true);
  }, [disabled]);

  return (
    <SelectWrapper ref={dropdownRef}>
      <SelectContainer isOpen={isOpen} disabled={disabled}>
        <IconContainer>{icon}</IconContainer>
        <Input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          translate="no"
        />
      </SelectContainer>

      {isOpen && !disabled && (
        <Dropdown>
          {defaultCountries.length > 0 || regularCountries.length > 0 ? (
            <>
              {defaultCountries.length > 0 && (
                <>
                  <SectionHeader>{t("popular_countries") || "Популярные"}</SectionHeader>
                  {defaultCountries.map(option => (
                    <DropdownOption
                      key={option.code}
                      isSelected={value === option.code}
                      onClick={() => handleOptionClick(option)}
                    >
                      <Flag>{option.flag}</Flag>
                      <CountryName>{option.name}</CountryName>
                      {value === option.code && <CheckIcon>✓</CheckIcon>}
                    </DropdownOption>
                  ))}
                </>
              )}

              {regularCountries.length > 0 && (
                <>
                  <SectionHeader>{t("all_countries") || "Все страны"}</SectionHeader>
                  {regularCountries.map(option => (
                    <DropdownOption
                      key={option.code}
                      isSelected={value === option.code}
                      onClick={() => handleOptionClick(option)}
                    >
                      <Flag>{option.flag}</Flag>
                      <CountryName>{option.name}</CountryName>
                      {value === option.code && <CheckIcon>✓</CheckIcon>}
                    </DropdownOption>
                  ))}
                </>
              )}
            </>
          ) : (
            <NoResults>{t("no_country")}</NoResults>
          )}
        </Dropdown>
      )}
    </SelectWrapper>
  );
};
